import { createFeatureSelector, createSelector } from '@ngrx/store';
import { handleActions } from 'redux-actions';
import {
  entityReducerMap,
  IEntityList,
  IEntityState,
  IFilteredLists,
  initialListState,
  mergeNewEntities,
} from '../entity-data/entity-data.reducer';
import {
  CLIENT_DOCUMENTS_SEARCH_PARAMS,
  DEFAULT_SEARCH_PARAMS,
  DocumentHistoryFilterTypesEnum,
} from './client-data.data';
import { EntityTypesEnum } from '../../app.enum';
import { List } from 'immutable';
import { IError, ISearchParams } from '../entity-data/entity-data.data';
import { Document } from '../document-data/document.model';
import {
  ActionsEnum,
  DeleteDocumentInClientDocuments,
  FetchClientDocuments,
  FetchClientDocumentsError,
  FilterClientDocuments,
  SetClientDocuments,
  ShowJobDialog,
  UpdateDocumentInClientDocuments,
} from './client-data.actions';

// feature name
export const FEATURE_NAME = 'client-data';

// state interface
export interface IClientDocumentList {
  documents: List<Document>;
  fetched: boolean;
  searchParams: ISearchParams;
  filter: DocumentHistoryFilterTypesEnum;
  error: IError;
}

export interface IState extends IEntityState {
  clientDocuments: IClientDocumentList;
  showJobDialog: boolean;
}

// initial state of store
export const initialClientDocumentListState: IClientDocumentList = {
  documents: null,
  fetched: false,
  searchParams: CLIENT_DOCUMENTS_SEARCH_PARAMS,
  filter: DocumentHistoryFilterTypesEnum.all_documents,
  error: null,
};

export const initialClientListState: IEntityList = {
  ...initialListState,
  searchParams: DEFAULT_SEARCH_PARAMS,
};

export const initialFilteredListsState: IFilteredLists = {
  all: initialClientListState,
  search: initialClientListState,
};

export const initialState: IState = {
  filteredLists: initialFilteredListsState,
  clientDocuments: initialClientDocumentListState,
  editingEntity: null,
  selectedEntities: null,
  masterError: null,
  dispatchedResponse: null,
  showJobDialog: false,
};

// reducers
const reducerFn = handleActions(
  {
    ...entityReducerMap<IState>(),
    [ActionsEnum.FETCH_CLIENT_DOCUMENTS]: function (
      state: IState,
      { payload }: FetchClientDocuments
    ): IState {
      const { filter } = payload;
      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: null,
        fetched: false,
        filter,
        error: null,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.FETCH_NEXT_CLIENT_DOCUMENTS_PAGE]: function (
      state: IState
    ): IState {
      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        fetched: false,
        error: null,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.FILTER_CLIENT_DOCUMENTS]: function (
      state: IState,
      { payload }: FilterClientDocuments
    ): IState {
      const { filter } = payload;
      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: null,
        fetched: false,
        filter,
        error: null,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.CLEAR_CLIENT_DOCUMENTS]: function (state: IState): IState {
      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: null,
        fetched: false,
        error: null,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.SET_CLIENT_DOCUMENTS]: function (
      state: IState,
      { payload }: SetClientDocuments
    ): IState {
      const { documents, searchParams, comparatorFn } = payload;

      let newDocuments = mergeNewEntities(
        state.clientDocuments.documents || List([]),
        documents,
        List<any>([]),
        true
      );

      if (comparatorFn) {
        newDocuments = newDocuments.sort(comparatorFn) as List<Document>;
      }

      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: newDocuments,
        searchParams,
        fetched: true,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.UPDATE_DOCUMENT_IN_CLIENT_DOCUMENTS]: function (
      state: IState,
      { payload }: UpdateDocumentInClientDocuments
    ): IState {
      const { document } = payload;
      const currentDocuments = state.clientDocuments.documents;
      const index: number = currentDocuments.findIndex(
        (doc: Document) => doc.id === document.id
      );
      const newDocuments: List<Document> =
        index > -1 ? currentDocuments.set(index, document) : currentDocuments;

      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: newDocuments,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.DELETE_DOCUMENT_IN_CLIENT_DOCUMENTS]: function (
      state: IState,
      { payload }: DeleteDocumentInClientDocuments
    ): IState {
      const { document } = payload;
      const currentDocuments = state.clientDocuments.documents;
      const index: number = currentDocuments.findIndex(
        (doc: Document) => doc.id === document.id
      );
      const newDocuments: List<Document> =
        index > -1 ? currentDocuments.remove(index) : currentDocuments;

      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        documents: newDocuments,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.FETCH_CLIENT_DOCUMENTS_ERROR]: function (
      state: IState,
      { payload }: FetchClientDocumentsError
    ): IState {
      const { error, data } = payload;
      const fetchError: IError = {
        error,
        data,
      };
      const clientDocuments: IClientDocumentList = {
        ...state.clientDocuments,
        fetched: true,
        error: fetchError,
      };

      return {
        ...state,
        clientDocuments,
      };
    },
    [ActionsEnum.SHOW_JOB_DIALOG]: function (
      state: IState,
      { payload }: ShowJobDialog
    ): IState {
      return {
        ...state,
        showJobDialog: payload.flag,
      };
    },
  } as any,
  initialState
);

// filter actions by entityType
export function reducer(state: IState = initialState, action: any) {
  return action.payload && action.payload.entityType === EntityTypesEnum.client
    ? reducerFn(state, action)
    : state;
}

// selectors
export const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export function getClientDocuments() {
  return createSelector(
    selectFeature,
    (state: IState) => state.clientDocuments.documents
  );
}

export function getClientDocumentsFetched() {
  return createSelector(
    selectFeature,
    (state: IState) => state.clientDocuments.fetched
  );
}

export function getClientDocumentsSearchParams() {
  return createSelector(
    selectFeature,
    (state: IState) => state.clientDocuments.searchParams
  );
}

export function getClientDocumentsFilter() {
  return createSelector(
    selectFeature,
    (state: IState) => state.clientDocuments.filter
  );
}

export function getClientDocumentsErrors() {
  return createSelector(
    selectFeature,
    (state: IState) => state.clientDocuments.error
  );
}

export function getShowJobDialog() {
  return createSelector(selectFeature, (state: IState) => state.showJobDialog);
}
